import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    setPersistence,
    browserLocalPersistence,
    inMemoryPersistence
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { api, register as apiRegister, clearCacheByPattern } from '../services/api';
import { auth } from '../firebase/config';
import { useNotification } from './NotificationContext';
import { event, setUserId } from '../utils/analytics';

const AuthContext = createContext({
    currentUser: null,
    loading: true,
    setCurrentUser: () => { },
    favorites: {
        rooms: [],
        users: []
    },
    blockedUsers: [],
    checkEmailVerification: () => Promise.resolve(false),
});

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [favorites, setFavorites] = useState({ rooms: [], users: [] });
    const [blockedUsers, setBlockedUsers] = useState([]);
    const [authInitialized, setAuthInitialized] = useState(false);
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    // APIコールのキャッシュと重複防止用のref
    const profileCache = useRef({
        data: null,
        timestamp: null,
        isLoading: false
    });
    const emailVerificationCache = useRef({
        lastUpdated: null,
        isUpdating: false
    });

    // キャッシュの有効期限（5分）
    const CACHE_DURATION = 5 * 60 * 1000;
    // メール認証更新の最小間隔（1分）
    const EMAIL_VERIFICATION_INTERVAL = 60 * 1000;

    // プロフィールデータを取得する関数（キャッシュ機能付き）
    const fetchProfileData = async (forceRefresh = false) => {
        const now = Date.now();

        // キャッシュが有効で、強制更新でない場合はキャッシュを返す
        if (!forceRefresh &&
            profileCache.current.data &&
            profileCache.current.timestamp &&
            (now - profileCache.current.timestamp) < CACHE_DURATION) {
            return profileCache.current.data;
        }

        // 既にロード中の場合は待機
        if (profileCache.current.isLoading) {
            return new Promise((resolve) => {
                const checkLoading = () => {
                    if (!profileCache.current.isLoading) {
                        resolve(profileCache.current.data);
                    } else {
                        setTimeout(checkLoading, 100);
                    }
                };
                checkLoading();
            });
        }

        try {
            profileCache.current.isLoading = true;
            const response = await api.get('/users/profile');

            // キャッシュを更新
            profileCache.current.data = response.data;
            profileCache.current.timestamp = now;

            return response.data;
        } catch (error) {
            console.error('Profile fetch error:', error);
            throw error;
        } finally {
            profileCache.current.isLoading = false;
        }
    };

    // メール認証状態をサーバーに更新する関数（重複防止機能付き）
    const updateEmailVerificationStatus = async (token) => {
        const now = Date.now();

        // 最近更新済みの場合はスキップ
        if (emailVerificationCache.current.lastUpdated &&
            (now - emailVerificationCache.current.lastUpdated) < EMAIL_VERIFICATION_INTERVAL) {
            return true;
        }

        // 既に更新中の場合はスキップ
        if (emailVerificationCache.current.isUpdating) {
            return true;
        }

        try {
            emailVerificationCache.current.isUpdating = true;
            const response = await fetch('/api/users/update-email-verification', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                emailVerificationCache.current.lastUpdated = now;
                return true;
            } else {
                console.error('メール認証状態の更新に失敗しました');
                return false;
            }
        } catch (error) {
            console.error('Error updating email verification status:', error);
            return false;
        } finally {
            emailVerificationCache.current.isUpdating = false;
        }
    };

    // メール認証状態を確認する関数（外部から呼び出し可能）
    const checkEmailVerification = async () => {
        if (!auth.currentUser) return false;

        try {
            // Firebaseユーザー情報をリロード
            await auth.currentUser.reload();
            const user = auth.currentUser;

            if (user && user.emailVerified) {
                // MongoDB側の状態も更新（重複防止機能付き）
                const token = await user.getIdToken(true);
                const updated = await updateEmailVerificationStatus(token);

                if (updated) {
                    // 状態が更新されたら、ユーザー情報も再取得（強制更新）
                    const profileData = await fetchProfileData(true);
                    const userData = {
                        ...user,
                        favorites: profileData.favorites || { rooms: [], users: [] },
                        blockedUsers: profileData.blockedUsers || [],
                        isProfileSetupCompleted: profileData.isProfileSetupCompleted || false,
                        emailVerified: true
                    };

                    setCurrentUser(userData);
                    setUserProfile(profileData);

                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('認証確認エラー:', error);
            return false;
        }
    };

    // 電話番号認証状態を確認する関数
    const checkPhoneVerification = async () => {
        if (!currentUser) return false;

        try {
            // プロフィールデータを強制更新して最新の電話番号認証状態を取得
            const profileData = await fetchProfileData(true);

            if (profileData.phoneVerified) {
                // 状態が更新されたら、ユーザー情報も再取得
                const userData = {
                    ...currentUser,
                    favorites: profileData.favorites || { rooms: [], users: [] },
                    blockedUsers: profileData.blockedUsers || [],
                    isProfileSetupCompleted: profileData.isProfileSetupCompleted || false,
                    phoneVerified: true,
                    phoneNumber: profileData.phoneNumber
                };

                setCurrentUser(userData);
                setUserProfile(profileData);

                return true;
            }
            return false;
        } catch (error) {
            console.error('電話番号認証確認エラー:', error);
            return false;
        }
    };

    // Firebase認証の永続性を設定
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                await setPersistence(auth, browserLocalPersistence);
                setAuthInitialized(true);
            } catch (error) {
                console.error('Firebase persistence error:', error);
                // フォールバックとしてinMemoryPersistenceを試す
                try {
                    await setPersistence(auth, inMemoryPersistence);
                    setAuthInitialized(true);
                } catch (fallbackError) {
                    console.error('Firebase fallback persistence error:', fallbackError);
                }
            }
        };

        initializeAuth();
    }, []);

    // 認証状態の監視は認証初期化後にのみ開始
    useEffect(() => {
        if (!authInitialized) return;

        let retryCount = 0;
        const MAX_RETRIES = 3;
        const RETRY_DELAY = 1000; // 1秒

        const setupAuthStateListener = () => {
            const unsubscribe = onAuthStateChanged(auth, async (user) => {
                if (user) {
                    try {
                        // トークンを強制的に更新
                        const token = await user.getIdToken(true);
                        localStorage.setItem('token', token);
                        sessionStorage.setItem('token', token); // バックアップとして sessionStorage にも保存

                        // Firebase側でメール認証が完了している場合のみ、MongoDB側も更新
                        if (user.emailVerified) {
                            // 重複防止機能付きでメール認証状態を更新
                            await updateEmailVerificationStatus(token);
                        }

                        // キャッシュ機能付きでユーザープロフィールを取得
                        const profileData = await fetchProfileData();
                        const userData = {
                            ...user,
                            favorites: profileData.favorites || { rooms: [], users: [] },
                            blockedUsers: profileData.blockedUsers || [],
                            isProfileSetupCompleted: profileData.isProfileSetupCompleted || false
                        };

                        setCurrentUser(userData);
                        setUserProfile(profileData);
                        setBlockedUsers(profileData.blockedUsers || []);
                        setFavorites(profileData.favorites || { rooms: [], users: [] });

                        // ログイン状態が変わったときにGoogle Analyticsにユーザー情報を設定
                        if (userData.uid) {
                            // ユーザーIDをハッシュ化してトラッキング（例：最初の8文字のみ使用）
                            const hashedId = userData.uid.substring(0, 8);
                            setUserId(hashedId);
                        }

                    } catch (error) {
                        console.error('Auth state change error:', error);
                        if (retryCount < MAX_RETRIES) {
                            retryCount++;
                            setTimeout(setupAuthStateListener, RETRY_DELAY);
                        } else {
                            // 基本的なユーザー情報だけでも設定
                            setCurrentUser(user);
                        }
                    }
                } else {
                    localStorage.removeItem('token');
                    sessionStorage.removeItem('token');
                    setCurrentUser(null);
                    setUserProfile(null);
                    setBlockedUsers([]);
                    setFavorites({ rooms: [], users: [] });

                    // キャッシュもクリア
                    profileCache.current = {
                        data: null,
                        timestamp: null,
                        isLoading: false
                    };
                    emailVerificationCache.current = {
                        lastUpdated: null,
                        isUpdating: false
                    };
                }
                setLoading(false);
            });

            return unsubscribe;
        };

        const unsubscribe = setupAuthStateListener();
        return () => unsubscribe();
    }, [authInitialized, navigate, showNotification]);

    const login = async (email, password) => {
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);

            // Google Analyticsへイベント送信
            event({
                category: 'User',
                action: 'Logged In',
                label: 'Email Login'
            });

            // トークンを強制的に更新して保存
            const token = await result.user.getIdToken(true);
            localStorage.setItem('token', token);
            sessionStorage.setItem('token', token);

            // ユーザープロフィールを即座に取得（強制更新）
            try {
                const profileData = await fetchProfileData(true);
                setUserProfile(profileData);
                setBlockedUsers(profileData.blockedUsers || []);
                setFavorites(profileData.favorites || { rooms: [], users: [] });
            } catch (error) {
                console.error('Profile fetch error during login:', error);
            }

            return result;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };

    const register = async (email, password) => {
        try {
            // Firebase認証
            const result = await createUserWithEmailAndPassword(auth, email, password);

            // Google Analyticsへイベント送信
            event({
                category: 'User',
                action: 'Signed Up',
                label: 'Email Signup'
            });

            // 認証が成功したら、currentUserを更新
            setCurrentUser(result.user);

            // トークンを取得してローカルストレージに保存
            const token = await result.user.getIdToken();
            localStorage.setItem('token', token);

            return result;  // resultをそのまま返す
        } catch (error) {
            console.error('Firebase registration error:', error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);

            // Google Analyticsへイベント送信
            event({
                category: 'User',
                action: 'Logged Out'
            });

            localStorage.removeItem('token');
            setUserProfile(null);
            navigate('/');
        } catch (error) {
            throw error;
        }
    };

    const resetPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error) {
            throw error;
        }
    };

    const updateProfile = async (profileData) => {
        try {
            const response = await api.put('/users/profile', profileData);

            // プロフィール更新後はキャッシュを無効化
            profileCache.current = {
                data: response.data,
                timestamp: Date.now(),
                isLoading: false
            };

            // 関連するキャッシュをクリア
            clearCacheByPattern('/users/profile');
            clearCacheByPattern(`/users/${currentUser?.uid}`);

            setUserProfile(response.data);

            // currentUserも更新（isProfileSetupCompletedを含む）
            if (currentUser) {
                setCurrentUser({
                    ...currentUser,
                    favorites: response.data.favorites || { rooms: [], users: [] },
                    blockedUsers: response.data.blockedUsers || [],
                    isProfileSetupCompleted: response.data.isProfileSetupCompleted || false,
                    // 電話番号認証状態も更新
                    phoneVerified: response.data.phoneVerified || false,
                    phoneNumber: response.data.phoneNumber || null
                });
            }

            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const value = {
        currentUser,
        userProfile,
        loading,
        register,
        login,
        logout,
        resetPassword,
        updateProfile,
        favorites,
        setFavorites,
        blockedUsers,
        setBlockedUsers,
        checkEmailVerification,
        checkPhoneVerification
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
} 